import { Type, type Static } from '@sinclair/typebox';
import {
  defineSchema,
  SystemFields,
  RelationField,
  RelationOne,
  StringField,
  TextField,
  TimestampField,
  DecimalField,
  BooleanField,
  IntegerField,
  JsonField,
} from '@ayocore/platform/common/helpers/schema-types';
import accountingDocumentTypeSchemaDefinition from './document-type.schema';
import accountingDocumentStatusSchemaDefinition from './document-status.schema';
import accountingAllocationTypeSchemaDefinition from './allocation-type.schema';
import accountingLegalEntitySchemaDefinition from './legal-entity.schema';
import accountingLegalEntityBankAccountSchemaDefinition from './legal-entity-bank-account.schema';
import accountingCurrencySchemaDefinition from './currency.schema';
import accountingWarehouseSchemaDefinition from './warehouse.schema';
import identityUserSchemaDefinition from '@ayocore/platform/identity/schemas/user.schema';
import dealSchemaDefinition from '@ayocore/erp/crm/schemas/deal.schema';
import partnerSchemaDefinition from '@ayocore/erp/crm/schemas/partner.schema';

import { name as moduleName } from '../module.json';
/**
 * Бухгалтерские документы (шапка).
 *
 * Содержит общую информацию и реквизиты документа: тип, статус, валюту,
 * суммы, привязку к контрагенту и ответственным лицам.
 */
export const AccountingDocumentSchema = Type.Object({
  ...SystemFields,

  document_type: RelationOne(accountingDocumentTypeSchemaDefinition),
  document_type_id: RelationField(accountingDocumentTypeSchemaDefinition, 'ID типа документа', {
    description: 'Определяет тип документа',
  }),

  document_status: RelationOne(accountingDocumentStatusSchemaDefinition),
  document_status_id: RelationField(
    accountingDocumentStatusSchemaDefinition,
    'ID статуса документа',
    {
      nullable: true,
      onDelete: 'SET NULL',
      description: 'Текущий статус документа в жизненном цикле.',
      relationName: 'document_status',
    }
  ),

  allocation_type: RelationOne(accountingAllocationTypeSchemaDefinition),
  allocation_type_id: RelationField(
    accountingAllocationTypeSchemaDefinition,
    'ID типа разнесения',
    {
      nullable: true,
      onDelete: 'SET NULL',
      description:
        'Тип разнесения документа (по сделкам, общие расходы, неразобранное, исключённое).',
      relationName: 'allocation_type',
    }
  ),

  organization: RelationOne(accountingLegalEntitySchemaDefinition),
  organization_id: RelationField(accountingLegalEntitySchemaDefinition, 'ID нашего юрлица', {
    description: 'Юрлицо, от имени которого оформлен документ.',
  }),

  counterparty: RelationOne(accountingLegalEntitySchemaDefinition),
  counterparty_id: RelationField(accountingLegalEntitySchemaDefinition, 'ID контрагента', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Контрагент по документу.',
  }),

  bank_account: RelationOne(accountingLegalEntityBankAccountSchemaDefinition),
  bank_account_id: RelationField(
    accountingLegalEntityBankAccountSchemaDefinition,
    'ID банковского счёта',
    {
      nullable: true,
      onDelete: 'SET NULL',
      description: 'Банковский счёт организации для расчётов по документу.',
    }
  ),

  number: StringField({
    title: 'Номер документа',
    nullable: true,
    maxLength: 100,
    description: 'Внутренний или внешний номер документа.',
  }),

  operation_date: TimestampField({
    title: 'Дата документа',
    description: 'Дата подписания или оформления документа.',
  }),

  invoice_number: StringField({
    title: 'Номер счёта',
    nullable: true,
    maxLength: 100,
    description:
      'Номер входящего счёта поставщика (счёт-фактура, проформа-инвойс) — внешний ' +
      'номер документа, в отличие от собственного `number`.',
  }),

  invoice_date: TimestampField({
    title: 'Дата счёта',
    nullable: true,
    description: 'Дата выставления входящего счёта или счёта-фактуры.',
  }),

  due_date: TimestampField({
    title: 'Плановая дата исполнения обязательств',
    nullable: true,
    description: 'Крайняя дата оплаты или исполнения обязательств.',
  }),

  contract_id: IntegerField({
    title: 'ID договора',
    nullable: true,
    description: 'Ссылка на запись договора (если ведётся в CRM).',
  }),

  deal: RelationOne(dealSchemaDefinition),
  deal_id: RelationField(dealSchemaDefinition, 'ID сделки', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Связь с сделкой из CRM.',
    relationName: 'deal',
  }),

  partner: RelationOne(partnerSchemaDefinition),
  partner_id: RelationField(partnerSchemaDefinition, 'ID партнера', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Связь с партнером из CRM.',
    relationName: 'partner',
  }),

  currency: RelationOne(accountingCurrencySchemaDefinition),
  currency_id: RelationField(accountingCurrencySchemaDefinition, 'ID валюты документа', {
    description: 'Валюта, в которой учитываются суммы.',
  }),

  warehouse: RelationOne(accountingWarehouseSchemaDefinition),
  warehouse_id: RelationField(accountingWarehouseSchemaDefinition, 'ID склада', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Склад для документов с товарными строками.',
    relationName: 'warehouse',
  }),

  amount: DecimalField({
    title: 'Сумма без налогов',
    precision: 14,
    scale: 2,
    default: 0,
    description: 'Сумма документа без учёта налогов.',
  }),

  tax_amount: DecimalField({
    title: 'Сумма налогов',
    precision: 14,
    scale: 2,
    default: 0,
    description: 'Общая сумма налогов по документу.',
  }),

  total_amount: DecimalField({
    title: 'Сумма с налогами',
    precision: 14,
    scale: 2,
    default: 0,
    description: 'Итоговая сумма документа с учётом налогов.',
  }),

  is_tax_inclusive: BooleanField({
    title: 'Цены включают налог',
    default: false,
    description: 'Если истина, суммы в строках указаны с учётом налога (tax-inclusive режим).',
  }),

  description: TextField({
    title: 'Описание',
    nullable: true,
    description: 'Свободное описание или комментарий.',
  }),

  is_urgent: BooleanField({
    title: 'Срочно',
    default: false,
    description: 'Признак срочного счёта/документа для приоритетной обработки.',
  }),

  posted: BooleanField({
    title: 'Проведён',
    default: false,
    description: 'Отражён ли документ в учёте (проведён).',
  }),

  posted_at: TimestampField({
    title: 'Дата проведения',
    nullable: true,
    description: 'Когда документ был проведён в учёте.',
  }),

  declined_at: TimestampField({
    title: 'Дата отклонения',
    nullable: true,
    description: 'Когда документ был отклонён и перестал быть актуальным.',
  }),

  owner_user: RelationOne(identityUserSchemaDefinition),
  owner_user_id: RelationField(identityUserSchemaDefinition, 'ID ответственного менеджера', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Ответственный менеджер за обработку документа.',
    relationName: 'owner_user',
  }),

  approved_at: TimestampField({
    title: 'Дата утверждения',
    nullable: true,
    description: 'Когда документ был утверждён ответственным лицом.',
  }),

  approved_by_user: RelationOne(identityUserSchemaDefinition),
  approved_by: RelationField(
    identityUserSchemaDefinition,
    'ID пользователя, утвердившего документ',
    {
      nullable: true,
      onDelete: 'SET NULL',
      description: 'Пользователь, утвердивший документ.',
      relationName: 'approved_by_user',
    }
  ),

  archived_at: TimestampField({
    title: 'Дата архивации',
    nullable: true,
    description:
      'Когда документ помещён в архив. NULL = активен. Признак для скрытия/показа в списках, учётного смысла не несёт.',
  }),

  archived_by: RelationOne(identityUserSchemaDefinition),
  archived_by_id: RelationField(
    identityUserSchemaDefinition,
    'ID пользователя, архивировавшего документ',
    {
      nullable: true,
      onDelete: 'SET NULL',
      description: 'Пользователь, поместивший документ в архив.',
      relationName: 'archived_by',
    }
  ),

  external_uid: StringField({
    title: 'Внешний идентификатор',
    nullable: true,
    maxLength: 100,
    description: 'Идентификатор документа во внешней системе (если синхронизируется).',
  }),

  metadata: JsonField(Type.Any(), {
    title: 'Дополнительные данные',
    nullable: true,
    description: 'JSON-поле для хранения произвольных структурированных данных.',
  }),
});

export type AccountingDocument = Static<typeof AccountingDocumentSchema>;

export default defineSchema({
  moduleName,
  tableName: 'documents',
  schema: AccountingDocumentSchema,
  additionalIndexes: [
    'document_type_id',
    'document_status_id',
    'allocation_type_id',
    'organization_id',
    'counterparty_id',
    'bank_account_id',
    'deal_id',
    'partner_id',
    'owner_user_id',
    'operation_date',
    'is_urgent',
    'declined_at',
    'posted',
    'warehouse_id',
  ],
  dependencies: [
    accountingDocumentTypeSchemaDefinition.tableName,
    accountingDocumentStatusSchemaDefinition.tableName,
    accountingAllocationTypeSchemaDefinition.tableName,
    accountingLegalEntitySchemaDefinition.tableName,
    accountingLegalEntityBankAccountSchemaDefinition.tableName,
    accountingCurrencySchemaDefinition.tableName,
    accountingWarehouseSchemaDefinition.tableName,
    identityUserSchemaDefinition.tableName,
    dealSchemaDefinition.tableName,
    partnerSchemaDefinition.tableName,
  ],
});
